import React, { useEffect, useRef } from 'react';
import { bool, object } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';

import NotFoundPage from '../../containers/NotFoundPage/NotFoundPage';
import PageBuilder from '../PageBuilder/PageBuilder';

export const CMSPageComponent = props => {
  const { params, pageAssetsData, inProgress, error, location } = props;
  const pageId = params?.pageId || props?.pageId;
  const data = pageAssetsData?.[pageId]?.data;
  const sections = data?.sections || [];
  const userScrolledRef = useRef(false);

  // When this CMS page's data arrives and there's a hash in the URL,
  // scroll to the target element. Handles initial load, images loading layout-shift,
  // and in-app navigation.
  useEffect(() => {
    const rawHash = location?.hash || (typeof window !== 'undefined' ? window.location.hash : '');
    if (!rawHash || sections.length === 0) {
      return;
    }

    const targetId = decodeURIComponent(rawHash.replace(/^#/, ''));
    userScrolledRef.current = false;

    // Detect if the user manually scrolled to avoid overriding their action
    const handleUserInteraction = () => {
      userScrolledRef.current = true;
    };
    window.addEventListener('wheel', handleUserInteraction, { passive: true });
    window.addEventListener('touchmove', handleUserInteraction, { passive: true });

    let timerIds = [];

    const getTopbarHeight = () => {
      if (typeof window === 'undefined') return 80;
      const isDesktop = window.innerWidth >= 1024;
      const propName = isDesktop ? '--topbarHeightDesktop' : '--topbarHeight';
      const parsed = parseInt(
        getComputedStyle(document.documentElement).getPropertyValue(propName) || (isDesktop ? '80' : '60'),
        10
      );
      return isNaN(parsed) ? 80 : parsed;
    };

    const scrollToTarget = (behavior = 'smooth') => {
      if (userScrolledRef.current) return true;

      const el =
        document.getElementById(targetId) ||
        document.querySelector(`[id="${targetId}"]`) ||
        document.querySelector(rawHash);

      if (!el) return false;

      const topbarHeight = getTopbarHeight();
      const rect = el.getBoundingClientRect();
      const currentScrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const targetTop = Math.max(0, rect.top + currentScrollTop - (topbarHeight + 20));

      // Only scroll if we are not already positioned near the target
      if (Math.abs(rect.top - (topbarHeight + 20)) > 15) {
        window.scrollTo({
          top: targetTop,
          behavior,
        });
      }
      return true;
    };

    // 1. Initial attempt on next frame
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        scrollToTarget('auto');
      });
    });

    // 2. Scheduled checks to adjust for images/fonts loading and expanding layout above the target
    const delays = [100, 250, 500, 800, 1200, 1800, 2500];
    delays.forEach(delay => {
      const id = setTimeout(() => {
        scrollToTarget(delay <= 250 ? 'auto' : 'smooth');
      }, delay);
      timerIds.push(id);
    });

    // 3. Re-adjust whenever images finish loading
    const handleImageLoad = () => {
      scrollToTarget('smooth');
    };
    window.addEventListener('load', handleImageLoad);
    const images = document.querySelectorAll('img');
    images.forEach(img => {
      if (!img.complete) {
        img.addEventListener('load', handleImageLoad, { once: true });
      }
    });

    return () => {
      window.removeEventListener('wheel', handleUserInteraction);
      window.removeEventListener('touchmove', handleUserInteraction);
      window.removeEventListener('load', handleImageLoad);
      timerIds.forEach(id => clearTimeout(id));
    };
  }, [sections, location?.hash]);

  if (!inProgress && error?.status === 404) {
    return <NotFoundPage staticContext={props.staticContext} />;
  }

  return (
    <PageBuilder
      pageAssetsData={data}
      inProgress={inProgress}
      schemaType="Article"
    />
  );
};

CMSPageComponent.propTypes = {
  pageAssetsData: object,
  inProgress: bool,
};

const mapStateToProps = state => {
  const { pageAssetsData, inProgress, error } = state.hostedAssets || {};
  return { pageAssetsData, inProgress, error };
};

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const CMSPage = compose(withRouter, connect(mapStateToProps))(CMSPageComponent);

export default CMSPage;
